/**
 * Types mirroring the research worker's integrations API
 * (`researchharness/integrations`). Ported verbatim from the Sensarra standalone
 * site so the Research connections tab renders the identical registry-driven
 * catalogue. The backend is the source of truth for which providers exist on
 * this surface and what fields they need; the frontend renders whatever the
 * catalogue returns.
 */

export type AuthType = 'none' | 'api_key' | 'mcp' | 'oauth';
export type Availability = 'ide_only' | 'hosted_only' | 'both';
export type DeployMode = 'ide' | 'hosted';

export interface CredentialField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  placeholder: string;
  help: string;
  default: string;
}

export interface SecretState {
  configured: boolean;
  last4: string;
  length: number;
  readable: boolean;
}

export interface Connection {
  provider_id: string;
  fields: Record<string, string>;
  secrets: Record<string, SecretState>;
  enabled: boolean;
  updated_at: string;
}

export interface Provider {
  id: string;
  name: string;
  category: string;
  auth_type: AuthType;
  availability: Availability;
  summary: string;
  docs_url: string;
  fields: CredentialField[];
  test_supported: boolean;
  instances: boolean;
  tags: string[];
  /** Only present for OAuth providers: has the operator configured client creds? */
  oauth_configured?: boolean;
  connection: Connection | null;
  /** Repeatable providers (for example custom MCP) return one row per saved instance. */
  connection_instances?: Connection[];
}

export interface Catalogue {
  schema_version: string;
  deploy_mode: DeployMode;
  providers: Provider[];
}

export interface TestResult {
  ok: boolean | null;
  detail: string;
  endpoint?: string;
}

export const CATEGORY_LABELS: Record<string, string> = {
  literature: 'Literature & discovery',
  reference_manager: 'Reference managers',
  research_agent: 'Research agents',
  identity: 'Researcher identity',
  data_repository: 'Data & output repositories',
  patents: 'Patents & IP',
  chemistry_biology: 'Chemistry & biology data',
  models: 'Models & datasets',
  mcp: 'Custom MCP connectors',
};

// Stable display order for the category sections.
export const CATEGORY_ORDER = [
  'identity',
  'literature',
  'reference_manager',
  'research_agent',
  'data_repository',
  'patents',
  'chemistry_biology',
  'models',
  'mcp',
];
